// src/utils/exportHelpers.js
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

//
// VENDOR MAP
//
const mapVendors = (vendors = []) => {
  const map = {};
  vendors.forEach((v) => {
    map[v.id] = v.name || v.fullname || v.business_name || "Unknown Vendor";
  });
  return map;
};

//
// CLEAN ROWS
//
const cleanRows = (rows, vendors = []) => {
  const vendorMap = mapVendors(vendors);

  return rows.map((r) => {
    const vendor =
      vendorMap[r.vendor_id] ||
      r.vendor_name ||
      r.vendor ||
      "Unknown Vendor";

    const customerPrice =
      Math.round(
        (Number(r.selling_price || 0) + Number(r.commission || 0)) / 100
      ) * 100;

    // PAYMENT HANDLING
    let payment = r.payment_method;
    if (payment === "multiple" && Array.isArray(r.payment_breakdown)) {
      const entries = r.payment_breakdown.map((p) => {
        // sanitize amount and method
        const num = Number(String(p.amount).replace(/[^\d.]/g, "")) || 0;
        const methodName = (p.method || "unknown").replace(/[^\w\s]/g, "");
        return `${methodName}: ₦${num.toLocaleString()}`;
      });
      payment = entries.join(", ");
    }

    return {
      Date: new Date(r.created_at).toLocaleString(),
      Product: r.product_name || r.product_id,
      Vendor: vendor,
      Qty: r.qty,
      Payment: payment,
      OrderMethod: r.order_method,
      CustomerPrice: customerPrice,
    };
  });
};

//
// CSV EXPORT
//
export const exportCSV = (rows, filename = "sales.csv", vendors = []) => {
  if (!rows || rows.length === 0) return;

  const cleaned = cleanRows(rows, vendors);
  const headers = Object.keys(cleaned[0]).join(",");
  const body = cleaned
    .map((row) =>
      Object.values(row)
        .map((val) => `"${val !== null ? val : ""}"`)
        .join(",")
    )
    .join("\n");

  const csv = `${headers}\n${body}`;
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = filename;
  link.click();
};

//
// EXCEL EXPORT
//
export const exportExcel = (rows, filename = "sales.xlsx", vendors = []) => {
  if (!rows || rows.length === 0) return;

  const cleaned = cleanRows(rows, vendors);
  const ws = XLSX.utils.json_to_sheet(cleaned);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Sales");
  XLSX.writeFile(wb, filename);
};

//
// PDF EXPORT
//
export const exportPDF = (rows, filename = "sales.pdf", vendors = [], logo = "/logo.png") => {
  if (!rows || rows.length === 0) return;

  const cleaned = cleanRows(rows, vendors);
  if (!cleaned.length) return;

  const headers = Object.keys(cleaned[0]);
  const data = cleaned.map((r) => Object.values(r));
  const paymentColIndex = headers.indexOf("Payment");

  const doc = new jsPDF({
    orientation: "landscape",
    unit: "pt",
    format: "a4",
  });

  try {
    doc.addImage(logo, "PNG", 40, 30, 80, 40);
  } catch {}

  // Watermark (removed as per user request to replace Deesoftwork branding, 
  // or I could keep it if they didn't explicitly say remove it from PDF, 
  // but "replace throughout the application" suggests removing it)
  /*
  doc.setFontSize(60);
  doc.setTextColor(200, 200, 200);
  doc.text(
    "DEESOFTWORK",
    doc.internal.pageSize.getWidth() / 2,
    doc.internal.pageSize.getHeight() / 2,
    { align: "center", angle: 45 }
  );
  */

  // Title
  doc.setFontSize(20);
  doc.setTextColor(0);
  doc.text("Sales Report", 200, 60);

  autoTable(doc, {
    head: [headers],
    body: data,
    startY: 100,
    styles: {
      fontSize: 10,
      cellPadding: 6,
      overflow: "linebreak",
    },
    columnStyles:
      paymentColIndex >= 0
        ? {
            [paymentColIndex]: {
              cellWidth: 300, // wide enough to show long payment strings
              overflow: "linebreak",
            },
          }
        : {},
    headStyles: { fillColor: [40, 40, 40], textColor: 255, fontStyle: "bold" },
    alternateRowStyles: { fillColor: [240, 240, 240] },
    margin: { top: 100, bottom: 50 },
    didDrawPage: (data) => {
      const pageCount = doc.internal.getNumberOfPages();
      const size = doc.internal.pageSize;

      // Footer
      doc.setFontSize(10);
      doc.setTextColor(100);
      doc.text(
        `Generated by Business Management System | Page ${data.pageNumber} of ${pageCount}`,
        size.width / 2,
        size.height - 10,
        { align: "center" }
      );
    },
  });

  doc.save(filename);
};
